import { type CTWRequestContext, type CTWState, useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext, usePatientQuery } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { getAiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_AI_CHART_REVIEW_SUMMARY } from '@ctw/shared/utils/query-keys';
import { retryWithExponentialBackoff } from '@ctw/shared/utils/request';
import { useState } from 'react';
import type { ChartReviewResponse } from './types';
import { translateUnixBreaksToHtml } from './utils';

const MAX_RETRIES = 5;
const BASE_DELAY = 2000;

interface GetOrPutSummaryViaNewApiArgs {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  upid: string;
  summaryType: string;
}

// get a summary via the new API, if not found, try generating a new summary
const getOrPutSummaryViaNewApi = async ({
  ctwFetch,
  requestContext,
  upid,
  summaryType,
}: GetOrPutSummaryViaNewApiArgs): Promise<Array<ChartReviewResponse>> => {
  const endpoint = `${getAiServiceBaseUrl({
    env: requestContext.env,
  })}/patients/${upid}/summary/${summaryType}`;

  // we use fetch directly here because we want to be able to handle the 404
  // and return an error summary
  const resp = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Content-Type': 'application/json',
      'Zus-Account': requestContext.builderId,
    },
    method: 'GET',
  });

  if (resp.ok) {
    const data: Array<ChartReviewResponse> = await resp.json();
    return data.map((summary) => {
      summary.hpi = translateUnixBreaksToHtml(summary.hpi);
      return summary;
    });
  }

  // if the response is a 404, try generating a new summary
  if (resp.status === 404) {
    return await triggerSummaryGeneration({
      ctwFetch,
      requestContext,
      upid,
      summaryType,
    });
  }

  throw new Error('Error fetching patient summary');
};

interface TriggerSummaryGenerationArgs {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  upid: string;
  summaryType: string;
}

const triggerSummaryGeneration = async ({
  ctwFetch,
  requestContext,
  upid,
  summaryType,
}: TriggerSummaryGenerationArgs): Promise<Array<ChartReviewResponse>> => {
  const endpoint = `${getAiServiceBaseUrl({ env: requestContext.env })}/patients/${upid}/summary/${summaryType}`;

  const resp = await ctwFetch<Array<ChartReviewResponse>>(endpoint, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Content-Type': 'application/json',
      'Zus-Account': requestContext.builderId,
    },
    method: 'POST',
  });

  // hack: for now just return the first summary. We'll be
  // returning an array of summaries soon.
  if (resp.data.length === 0) {
    return [ErrorSummary];
  }

  return resp.data.map((summary) => {
    summary.hpi = translateUnixBreaksToHtml(summary.hpi);
    return summary;
  });
};

export const ErrorSummary: ChartReviewResponse = {
  title: 'Error',
  hpi: 'An error occurred while fetching the summary',
  citations: [],
};

export const NotFoundSummary: ChartReviewResponse = {
  title: 'Not Found',
  hpi: 'The summary was not found. Please contact support',
  citations: [],
};

export const useSummariesForPatient = (summaryType: string | null) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const telemetry = useTelemetry();

  const { requestContext, ctwFetch } = useCTW();
  const { patient } = usePatientContext();

  const {
    data: document,
    isLoading,
    error,
    refetch,
  } = usePatientQuery({
    queryId: QUERY_KEY_AI_CHART_REVIEW_SUMMARY,
    queryKey: [summaryType],
    queryFn: async ({
      requestContext: queryRequestContext,
      patient: queryPatient,
    }): Promise<Array<ChartReviewResponse>> => {
      if (!summaryType) {
        return [ErrorSummary];
      }
      try {
        const upid = queryPatient.UPID ?? '';

        return await getOrPutSummaryViaNewApi({
          ctwFetch,
          requestContext: queryRequestContext,
          upid,
          summaryType,
        });
      } catch (e) {
        telemetry.trackError({
          message: `Error fetching patient summary (in queryFn) for UPID: ${queryPatient.UPID}, Type: ${summaryType}`,
          error: e as Error,
        });
        return [ErrorSummary];
      }
    },
  });

  const getSummaries = async (requestedSummaryType: string | null) => {
    if (!requestedSummaryType) {
      return;
    }
    setIsGenerating(true);
    try {
      const upid = patient.UPID || 'unknown-upid';

      await getOrPutSummaryViaNewApi({
        ctwFetch,
        requestContext,
        upid,
        summaryType: requestedSummaryType,
      });

      // Wait for the summary to be generated
      return retryWithExponentialBackoff(
        async () => {
          return await refetch();
        },
        MAX_RETRIES,
        BASE_DELAY,
      );
    } catch (e) {
      telemetry.trackError({ message: 'Error generating patient summary', error: e });
      throw new Error('Error generating summary');
    } finally {
      setIsGenerating(false);
    }
  };

  const regenerateSummaries = async (requestedSummaryType: string | null) => {
    if (!requestedSummaryType) {
      return;
    }
    setIsGenerating(true);
    try {
      const upid = patient.UPID || 'unknown-upid';
      // Get the new summary directly from the POST response
      const newSummaries = await triggerSummaryGeneration({
        ctwFetch,
        requestContext,
        upid,
        summaryType: requestedSummaryType,
      });

      // Force a refetch to update the cache, but don't wait for it or use its results
      void refetch();

      // Return the already-generated summary from the POST
      return newSummaries;
    } catch (e) {
      telemetry.trackError({ message: 'Error regenerating patient summary', error: e });
      throw new Error('Error regenerating summary');
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    document,
    isLoading,
    isGenerating,
    error,
    getSummaries,
    regenerateSummaries,
  };
};
